import { faCheck, faEye, faEyeSlash, faTimes, faTimesCircle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import moment from 'moment';
import React from 'react';
import { FormattedMessage, WrappedComponentProps, injectIntl } from 'react-intl';
import {
    Alert,
    Button,
    ButtonGroup,
    Checkbox,
    CheckboxGroup,
    ControlLabel,
    DatePicker,
    FlexboxGrid,
    Form,
    FormControl,
    FormGroup,
    InputNumber,
    Message,
    Modal,
    SelectPicker,
    Toggle,
} from 'rsuite';
import { sensorPlaceTypeHandler } from '../../../../handlers/sensorPlaceType/sensorPlaceType.handler';
import { authHeader } from '../../../../redux/helpers';
import { axiosService } from '../../../../redux/services';

type Props = {
    service: any;
    show: boolean;
    onHide: Function;
    geoJSON: number[];
} & WrappedComponentProps;

type State = {
    loading: boolean;
    formValue: FormValue;
    hasError: boolean;
    sensorPlaceTypes: { label: string; value: number; image: Buffer; imageType: string }[];
};

type FormValue = {
    name: string;
    nbTotPlace: number;
    resetTime: any;
    isResettable: boolean;
    countingOnLevel: boolean;
    isShow: boolean;
    displayValue: string[];
    sensorPlaceType: number | null;
    isShowOnZone: number;
};

class CreatePassCountModal extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);

        this.state = {
            loading: false,
            formValue: {
                name: '',
                nbTotPlace: 100,
                countingOnLevel: true,
                resetTime: moment().startOf('day').format('LLLL'),
                isResettable: false,
                isShow: true,
                displayValue: [],
                sensorPlaceType: null,
                isShowOnZone: 99,
            },
            hasError: false,
            sensorPlaceTypes: [],
        };
    }

    componentDidMount = () => {
        axiosService
            .getAxios()
            .get('/sensor-place-type', { headers: authHeader() })
            .then(sensorPlaceResponse => {
                this.setState({
                    sensorPlaceTypes: sensorPlaceResponse.data.map(placeType => {
                        const pt = sensorPlaceTypeHandler(placeType);
                        return {
                            label: pt.label,
                            value: pt.id,
                            image: pt.placeType.image,
                            imageType: pt.placeType.imageType,
                        };
                    }),
                });
            });
    };

    handleChange = formValues => {
        this.setState({
            formValue: {
                name: formValues.name,
                nbTotPlace: parseInt(formValues.nbTotPlace),
                countingOnLevel: formValues.countingOnLevel,
                resetTime: formValues.resetTime,
                isResettable: formValues.isResettable,
                isShow: formValues.isShow,
                displayValue: formValues.displayValue,
                sensorPlaceType: formValues.sensorPlaceType,
                isShowOnZone: formValues.isShowOnZone ? 99 : 0,
            },
        });
    };

    createPassCount = () => {
        this.setState({
            loading: true,
        });

        const {
            name,
            nbTotPlace,
            resetTime,
            isResettable,
            isShow,
            displayValue,
            countingOnLevel,
            sensorPlaceType,
            isShowOnZone,
        } = this.state.formValue;

        axiosService
            .getAxios()
            .post(
                '/map-vehicle-counters',
                {
                    name,
                    nbTotPlace,
                    isResettable,
                    resetTime: isResettable
                        ? moment(resetTime).set('seconds', 0).diff(moment().startOf('day'), 'seconds')
                        : 0,
                    isShow,
                    displayValue,
                    countingOnLevel,
                    geoJSON: this.props.geoJSON,
                    levelId: this.props.service.machine.context.levelId,
                    id_sensorPlaceType: sensorPlaceType,
                    displayOnZone: isShowOnZone ? 99 : 0,
                },
                {
                    headers: authHeader(),
                }
            )
            .then(() => {
                this.setState({
                    formValue: {
                        name: '',
                        countingOnLevel: true,
                        nbTotPlace: 100,
                        resetTime: 0,
                        isResettable: false,
                        isShow: true,
                        displayValue: [],
                        sensorPlaceType: null,
                        isShowOnZone: 99,
                    },
                });

                Alert.success(this.props.intl.formatMessage({ id: 'map.passCount.createPassCount.success' }));

                this.cancelCreation(true);
            })
            .catch(err => {
                Alert.error(
                    this.props.intl.formatMessage(
                        { id: 'map.passCount.createPassCount.error' },
                        {
                            error: err,
                        }
                    )
                );
            })
            .finally(() => {
                this.setState({
                    loading: false,
                });
            });
    };

    cancelCreation = (shouldReload: boolean = false) => {
        this.setState({
            formValue: {
                name: '',
                nbTotPlace: 100,
                countingOnLevel: true,
                resetTime: 0,
                isResettable: false,
                isShow: true,
                displayValue: [],
                sensorPlaceType: null,
                isShowOnZone: 99,
            },
        });

        this.props.onHide(shouldReload);
    };

    render() {
        return (
            <Modal show={this.props.show} backdrop="static" onHide={() => this.cancelCreation(false)} size="xs">
                <Modal.Header>
                    <Modal.Title>
                        <FormattedMessage id="map.passCount.createPassCount" />
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {this.state.hasError && (
                        <Message
                            className="margin-bottom-15"
                            type="error"
                            description={this.props.intl.formatMessage({
                                id: 'map.passCount.createPassCountError',
                            })}
                        />
                    )}

                    <Form fluid onChange={values => this.handleChange(values)} formValue={this.state.formValue}>
                        <FormGroup>
                            <ControlLabel>
                                <FormattedMessage id="map.passCount.form.name" />
                            </ControlLabel>
                            <FormControl
                                data-cy="map-passCount-create-name"
                                className="form-margin"
                                name="name"
                                disabled={this.state.loading}
                            />
                        </FormGroup>

                        <FormGroup>
                            <ControlLabel>
                                <FormattedMessage id="map.passCount.form.sensorPlaceType" />
                            </ControlLabel>
                            <FormControl
                                className="form-margin"
                                name="sensorPlaceType"
                                data-cy="map-passCount-create-sensorPlaceType"
                                disabled={this.state.loading}
                                accepter={SelectPicker}
                                data={this.state.sensorPlaceTypes}
                                renderMenuItem={(_, item: any) => {
                                    return (
                                        <FlexboxGrid align="middle">
                                            <FlexboxGrid.Item data-cy="map-passCount-create-type">
                                                <img
                                                    alt={item.label}
                                                    style={{ height: 20 }}
                                                    src={`data:image/${item.imageType};base64, ${item.image}`}
                                                />
                                            </FlexboxGrid.Item>
                                            <FlexboxGrid.Item>{item.label}</FlexboxGrid.Item>
                                        </FlexboxGrid>
                                    );
                                }}
                                renderValue={(_, item: any) => {
                                    return (
                                        <FlexboxGrid align="middle">
                                            <FlexboxGrid.Item>
                                                <img
                                                    alt={item.label}
                                                    style={{ height: 20 }}
                                                    src={`data:image/${item.imageType};base64, ${item.image}`}
                                                />
                                            </FlexboxGrid.Item>
                                            <FlexboxGrid.Item>{item.label}</FlexboxGrid.Item>
                                        </FlexboxGrid>
                                    );
                                }}
                                cleanable={false}
                            />
                        </FormGroup>

                        <FormGroup>
                            <ControlLabel>
                                <FormattedMessage id="map.passCount.form.nbToPlace" />
                            </ControlLabel>
                            <FormControl
                                className="form-margin"
                                data-cy="map-passCount-create-nbToPlace"
                                name="nbTotPlace"
                                accepter={InputNumber}
                                min={0}
                                style={{ width: '100%' }}
                                disabled={this.state.loading}
                            />
                        </FormGroup>
                        <FormGroup>
                            <ControlLabel>
                                <FormattedMessage id="map.passCount.form.isResettable" />
                            </ControlLabel>
                            <FormControl
                                className="form-margin"
                                data-cy="map-passCount-create-isResettable"
                                name="isResettable"
                                defaultChecked={this.state.formValue.isResettable}
                                accepter={Toggle}
                                checkedChildren={<FontAwesomeIcon icon={faCheck} />}
                                unCheckedChildren={<FontAwesomeIcon icon={faTimes} />}
                                disabled={this.state.loading}
                            />
                        </FormGroup>
                        {this.state.formValue.isResettable && (
                            <FormGroup>
                                <ControlLabel>
                                    <FormattedMessage id="map.passCount.form.resetTime" />
                                </ControlLabel>
                                <FormControl
                                    className="form-margin"
                                    data-cy="map-passCount-create-resetTime"
                                    name="resetTime"
                                    accepter={DatePicker}
                                    ranges={[]}
                                    format="HH:mm"
                                    disabled={this.state.loading}
                                />
                            </FormGroup>
                        )}
                        <FormGroup>
                            <ControlLabel>
                                <FormattedMessage id="map.passCount.form.countingOnLevel" />
                            </ControlLabel>
                            <FormControl
                                className="form-margin"
                                name="countingOnLevel"
                                accepter={Toggle}
                                defaultChecked={this.state.formValue.countingOnLevel}
                                checkedChildren={<FontAwesomeIcon icon={faCheck} />}
                                unCheckedChildren={<FontAwesomeIcon icon={faTimes} />}
                                disabled={this.state.loading}
                            />
                        </FormGroup>

                        <FormGroup>
                            <ControlLabel>
                                <FormattedMessage id="map.passCount.form.isShow" />
                            </ControlLabel>
                            <FormControl
                                className="form-margin"
                                name="isShow"
                                data-cy="map-passCount-create-isShow"
                                accepter={Toggle}
                                defaultChecked={this.state.formValue.isShow}
                                checkedChildren={<FontAwesomeIcon icon={faEye} />}
                                unCheckedChildren={<FontAwesomeIcon icon={faEyeSlash} />}
                                disabled={this.state.loading}
                            />
                        </FormGroup>

                        <FormGroup>
                            <ControlLabel>
                                <FormattedMessage id="map.passCount.form.isShowOnZone" />
                            </ControlLabel>
                            <FormControl
                                className="form-margin"
                                name="isShowOnZone"
                                data-cy="map-passCount-create-isShowOnZone"
                                accepter={Toggle}
                                defaultChecked={this.state.formValue.isShowOnZone === 99}
                                checkedChildren={<FontAwesomeIcon icon={faEye} />}
                                unCheckedChildren={<FontAwesomeIcon icon={faEyeSlash} />}
                                disabled={this.state.loading}
                            />
                        </FormGroup>

                        <FormGroup>
                            <ControlLabel>
                                <FormattedMessage id="map.passCount.form.displayValue" />
                            </ControlLabel>
                            <FormControl
                                className="form-margin"
                                name="displayValue"
                                data-cy="map-passCount-create-displayValue"
                                accepter={CheckboxGroup}
                                inline
                                disabled={this.state.loading}>
                                <Checkbox value={'IN'}>IN</Checkbox>
                                <Checkbox value={'OUT'}>OUT</Checkbox>
                                <Checkbox value={'OCCUPED'}>FREE</Checkbox>
                            </FormControl>
                        </FormGroup>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <ButtonGroup>
                        <Button
                            onClick={() => this.cancelCreation(false)}
                            data-cy="map-passCount-createModal-cancel"
                            color="red"
                            disabled={this.state.loading}>
                            <FontAwesomeIcon icon={faTimesCircle} />
                        </Button>
                        <Button
                            onClick={this.createPassCount}
                            data-cy="map-passCount-createModal-valid"
                            appearance="primary"
                            loading={this.state.loading}>
                            <FontAwesomeIcon icon={faCheck} />
                        </Button>
                    </ButtonGroup>
                </Modal.Footer>
            </Modal>
        );
    }
}

export default injectIntl(CreatePassCountModal);
